import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase/client';
import { DepartmentMember } from '@/types/departments';
import { useToast } from './useToast';

export const useDepartmentMembers = (departmentId: string) => {
  const [members, setMembers] = useState<DepartmentMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const isMounted = useRef(true);
  const retryTimeoutRef = useRef<NodeJS.Timeout>();

  const fetchMembers = useCallback(async (retryCount = 0) => {
    if (!departmentId || !isMounted.current) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Get department workers with their user details
      const { data: users, error: workersError } = await supabase
        .from('users')
        .select('*')
        .eq('department_id', departmentId)
        .eq('role', 'worker')
        .eq('status', 'active');

      if (workersError) throw workersError;

      const transformedMembers = (users || []).map(user => ({
        id: user.id,
        firstName: user.full_name?.split(' ')[0] || '',
        lastName: user.full_name?.split(' ').slice(1).join(' ') || '',
        email: user.email,
        role: user.role,
        departmentRole: 'member'
      }));

      if (isMounted.current) {
        setMembers(transformedMembers);
        setError(null);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      if (isMounted.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch members');
        
        // Retry logic with exponential backoff
        if (retryCount < 3) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
          retryTimeoutRef.current = setTimeout(() => {
            fetchMembers(retryCount + 1);
          }, delay);
        }
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [departmentId, toast]);

  useEffect(() => {
    isMounted.current = true;

    // Initial fetch
    fetchMembers();

    // Set up realtime subscription
    channelRef.current = supabase
      .channel(`department_members_${departmentId}`)
      .on(
        'postgres_changes',
        { 
          event: '*', 
          schema: 'public', 
          table: 'users',
          filter: `department_id=eq.${departmentId}`
        },
        () => {
          if (isMounted.current) {
            fetchMembers();
          }
        }
      )
      .subscribe();

    // Cleanup function
    return () => {
      isMounted.current = false;
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [departmentId, fetchMembers]);

  return {
    members,
    isLoading,
    error,
    refetch: fetchMembers
  };
};